import { useEffect, useState } from 'react'
import { useAppStore } from 'Store/App'
import { useProjectStore } from 'Store/Project'
import { shallow } from 'zustand/shallow'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'
import { extractScannedCode, extractSoNo, extractItemId } from 'Utils/extractScannedCode'
import { inferScanTypeFromCode, type ScanInventoryType } from 'Utils/scanInventoryType'

type ExternalApiStatus = 'not_sent' | 'success' | 'failed'

interface ScanReceiveTestResult {
  qrcode: string
  serial: string
  type: ScanInventoryType
  soNo?: string
  itemId?: string
  responseStatus: 'success' | 'error'
  exists: boolean
  ambiguous?: boolean
  matchCount?: number
  inventoryCode?: string
  inventoryCodeD365?: string
  serialType?: string
  serialTypeSource?: string
  inventory?: any
  externalApiStatus?: ExternalApiStatus
  externalApiStatusReason?: string | null
  message?: string
}

const ViewModel = () => {
  const setShowBackButton = useAppStore(state => state.setShowBackButton, shallow)
  const scanReceiveTest = useProjectStore(state => state.scanReceivePileTest, shallow)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [scanCode, setScanCode] = useState('')
  const [isUseCamera, setIsUseCamera] = useState(false)
  const [hasCamera, setHasCamera] = useState(false)
  const [scanResult, setScanResult] = useState<ScanReceiveTestResult | null>(null)

  const submitScan = async (rawValue: string) => {
    const qrcode = rawValue.trim()
    const serial = extractScannedCode([{ rawValue: qrcode }])
    const type = inferScanTypeFromCode(qrcode)
    const soNo = extractSoNo(qrcode)
    const itemId = extractItemId(qrcode)
    if (!serial) {
      toast.error('กรุณาระบุรหัสเสา')
      return
    }

    setIsSubmitting(true)
    const { success, data } = await scanReceiveTest({
      inventoryCode: serial,
      type,
      soNo,
      itemId,
    })

    if (success) {
      setScanResult({
        qrcode,
        serial,
        type,
        soNo,
        itemId: data?.itemId ?? itemId,
        responseStatus: 'success',
        exists: Boolean(data?.exists),
        ambiguous: Boolean(data?.ambiguous),
        matchCount: data?.matchCount,
        inventoryCode: data?.inventoryCode,
        inventoryCodeD365: data?.inventoryCodeD365,
        serialType: data?.serialType,
        serialTypeSource: data?.serialTypeSource,
        inventory: data?.inventory,
        externalApiStatus: data?.externalApiStatus,
        externalApiStatusReason: data?.externalApiStatusReason,
        message: data?.message,
      })
      toast.success('ทดสอบสแกนเสร็จสิ้น')
    } else {
      const payload = typeof data === 'string' ? { message: data } : (data ?? {})
      setScanResult({
        qrcode,
        serial,
        type,
        soNo,
        itemId: payload?.itemId ?? itemId,
        responseStatus: 'error',
        exists: Boolean(payload?.exists),
        ambiguous: Boolean(payload?.ambiguous),
        matchCount: payload?.matchCount,
        inventoryCode: payload?.inventoryCode,
        inventoryCodeD365: payload?.inventoryCodeD365,
        serialType: payload?.serialType,
        serialTypeSource: payload?.serialTypeSource,
        inventory: payload?.inventory,
        externalApiStatus: payload?.externalApiStatus,
        externalApiStatusReason: payload?.externalApiStatusReason,
        message: payload?.message ?? 'ทดสอบสแกนไม่สำเร็จ',
      })
      toast.error(payload?.message ?? 'ทดสอบสแกนไม่สำเร็จ')
    }

    setIsSubmitting(false)
  }

  const handleScan = (scanData: any) => {
    if (scanData) {
      const rawValue = scanData[0].rawValue.trim()
      const serial = extractScannedCode(scanData)
      Swal.fire({
        title: 'ยืนยันทดสอบสแกน?',
        text: `คุณสแกนรหัส: ${serial}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'ยืนยัน',
        cancelButtonText: 'ยกเลิก',
      }).then((result: any) => {
        if (result.isConfirmed) {
          submitScan(rawValue)
        }
      })
    }
  }

  const toggleUseCamera = () => {
    const nextValue = !isUseCamera
    if (!hasCamera) {
      toast.error('ไม่พบกล้อง')
      return
    }
    setIsUseCamera(nextValue)
  }

  useEffect(() => {
    setShowBackButton(true)
    return () => {
      setShowBackButton(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    async function checkCameraAvailability () {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices()
        setHasCamera(devices.some(device => device.kind === 'videoinput'))
      } catch (error) {
        console.error('Error checking camera availability:', error)
      }
    }

    checkCameraAvailability()
  }, [])

  return {
    isSubmitting,
    isUseCamera,
    scanCode,
    handleScan,
    hasCamera,
    setScanCode,
    toggleUseCamera,
    scanResult,
  }
}

export default ViewModel
